import {
	QuestionChoice,
	variantTypes,
	parseTemplateStringIntoSegments,
	TemplateSegmentType,
	convertTemplateSegmentsToTemplateString,
	TemplateSegment,
	getImageDropdownKey,
} from '@mission.io/question-toolkit'
import { QuestionsForm } from '@mission.io/question-views'
import { EditableQuestion, MediaToUpload, Media } from './model'

export enum UPDATE_QUESTION_ACTIONS {
	DELETE_QUESTION = 'DELETE_QUESTION',
	DUPLICATE_QUESTION = 'DUPLICATE_QUESTION',
	CHANGE_ANSWER = 'CHANGE_ANSWER',
	CHANGE_QUESTION = 'CHANGE_QUESTION',
	CHANGE_QUESTION_IMAGE = 'CHANGE_QUESTION_IMAGE',
	CHANGE_MEDIA_CHOICE = 'CHANGE_MEDIA_CHOICE',
	CHANGE_ANSWER_CHOICE = 'CHANGE_ANSWER_CHOICE',
	DELETE_QUESTION_IMAGE = 'DELETE_QUESTION_IMAGE',
	DELETE_IMG_CHOICE = 'DELETE_IMG_CHOICE',
	CHANGE_CHOICE = 'CHANGE_CHOICE',
	ADD_CHOICE = 'ADD_CHOICE',
	DELETE_CHOICE = 'DELETE_CHOICE',
	ADD_DROPDOWN = 'ADD_DROPDOWN',
	DELETE_DROPDOWN = 'DELETE_DROPDOWN',
	CHANGE_TEMPLATE = 'CHANGE_TEMPLATE',
}

export type UpdateQuestionAction = QuestionsForm.UpdateQuestionAction

// function that find the media with type: 'IMAGE' and updates to the new one
function findMediaImgAndUpdate(media: Media[], payload: MediaToUpload) {
	const index: number | undefined = media.findIndex(
		mediaItem => mediaItem.type === 'IMAGE'
	)
	// if IMAGE already in the media, replace it with the new one:
	if (index && index >= 0) {
		media[index] = payload
	} else {
		// if not, just add payload to the media arr
		media = [...media, payload]
	}
	return media
}

/**
 * generateTemplateDif - generate an object explaining how to convert the previous template into the current template
 *
 * @param {TemplateSegment[]} previousTemplateSegments - the previous version of the template
 * @param {TemplateSegment[]} currentSegments - the new version of the template
 *
 * @return {QuestionsForm.ChangeTemplateDiff} - a diff explaining how to update the previous template
 */
export function generateTemplateDif(
	previousTemplateSegments: TemplateSegment[],
	currentSegments: TemplateSegment[]
): QuestionsForm.ChangeTemplateDiff {
	const previousInputIds: Set<string> = new Set()
	previousTemplateSegments.forEach(segment => {
		if (segment.type === TemplateSegmentType.Input) {
			previousInputIds.add(segment.inputId)
		}
	})

	const currentInputIds: string[] = []
	const currentInputIdCount: { [inputId: string]: number } = {}
	currentSegments.forEach(segment => {
		if (segment.type === TemplateSegmentType.Input) {
			const inputId = segment.inputId
			currentInputIds.push(inputId)
			if (!currentInputIdCount[inputId]) {
				currentInputIdCount[inputId] = 0
			}
			currentInputIdCount[inputId] += 1
		}
	})

	let seenIdCount = 0

	const insertedIds: string[] = []
	const renamedIds: { [oldInputId: string]: string } = {}

	const newTemplate = convertTemplateSegmentsToTemplateString(
		currentSegments.map(segment => {
			if (segment.type === TemplateSegmentType.Input) {
				const inputId = segment.inputId
				const newInputId = String(++seenIdCount)
				if (previousInputIds.has(inputId)) {
					// try to keep the mapping the same
					previousInputIds.delete(inputId)
					renamedIds[inputId] = newInputId
				} else {
					insertedIds.push(newInputId)
				}
				return { ...segment, inputId: newInputId }
			}
			return segment
		})
	)

	return {
		insertedIds,
		renamedIds,
		deletedIds: Array.from(previousInputIds),
		newTemplate,
	}
}

/**
 * applyClozeDropdownDiff - apply the diff to the given cloze dropdown to bring it up to date
 *
 * @param {QuestionsForm.EditableQuestion} question - the question to apply the diff to
 * @param {QuestionsForm.ChangeTemplateDiff} diff - the diff to apply
 *
 * @return {boolean} - true if changes occurred, false otherwise
 */
function applyClozeDropdownDiff(
	question: QuestionsForm.EditableQuestion,
	diff: QuestionsForm.ChangeTemplateDiff
): boolean {
	if (question.type !== variantTypes.CLOZE_DROPDOWN) {
		return false
	}

	const { deletedIds, renamedIds, insertedIds, newTemplate } = diff

	const oldTemplateChoices: {
		[templateResponseKey: string]: QuestionChoice[]
	} = {}
	question.dropdowns.forEach(
		({ templateResponseKey, choices }) =>
			(oldTemplateChoices[templateResponseKey] = choices)
	)
	deletedIds.forEach(deletedId => delete oldTemplateChoices[deletedId])

	const updatedTemplateChoices: {
		[templateResponseKey: string]: QuestionChoice[]
	} = {}
	Object.keys(renamedIds).forEach(originalId => {
		const newOriginalId = renamedIds[originalId]
		// @ts-expect-error TS2538 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		updatedTemplateChoices[newOriginalId] = oldTemplateChoices[originalId] ?? []
	})

	insertedIds.forEach(newInputId => (updatedTemplateChoices[newInputId] = []))

	// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
	question.dropdowns = Object.keys(updatedTemplateChoices).map(inputId => ({
		templateResponseKey: inputId,
		choices: updatedTemplateChoices[inputId],
	}))
	question.template = newTemplate

	return true
}

/**
 * Mutable update function for a question
 * @param {} state
 */
export function updateQuestion(
	question: QuestionsForm.EditableQuestion,
	action: UpdateQuestionAction
): boolean {
	switch (action.type) {
		case UPDATE_QUESTION_ACTIONS.CHANGE_ANSWER: {
			const payload = action.payload
			if (question.type === 'FREE_RESPONSE') {
				question.answer = payload
				return true
			}
			break
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_ANSWER_CHOICE: {
			const { index, dropdownIndex = -1 } = action.payload
			const choices = getChoices(question, dropdownIndex)

			let updated = false

			if (question.type === variantTypes.MULTIPLE_SELECT) {
				if (choices[index]) {
					choices[index].answer = !choices[index].answer
					updated = true
				}
			} else {
				if (choices[index]) {
					choices.forEach(
						(choice, pos: number) => (choice.answer = pos === index)
					)
					updated = true
				}
			}
			return updated
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_CHOICE: {
			const { index, value, dropdownIndex = -1 } = action.payload

			const choices = getChoices(question, dropdownIndex)
			const choice = choices[index]
			if (choice) {
				choice.text = value
				return true
			} else return false
		}
		case UPDATE_QUESTION_ACTIONS.ADD_CHOICE: {
			const { choice, dropdownIndex = -1 } = action.payload

			const choices = getChoices(question, dropdownIndex)
			choice.answer = choices.length < 1
			choices.push(choice)

			return true
		}
		case UPDATE_QUESTION_ACTIONS.DELETE_CHOICE: {
			const { index, dropdownIndex = -1 } = action.payload

			const choices = getChoices(question, dropdownIndex)
			choices.splice(index, 1)
			return true
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION: {
			const payload = action.payload
			if (question.type === variantTypes.CLOZE_DROPDOWN) return false

			question.phrase = payload
			return true
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_TEMPLATE: {
			return applyClozeDropdownDiff(question, action.payload)
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_QUESTION_IMAGE: {
			const payload: QuestionsForm.ChangeQuestionImage['payload'] =
				action.payload

			question.media = question.media?.filter(
				(mediaItem: Media) => mediaItem.type !== 'IMAGE'
			)
			if (question.type === variantTypes.IMAGE_LABELING) {
				question.dropdowns = []
				question.imageProperties = {
					width: payload.width || 0,
					height: payload.height || 0,
				}
			}
			if (question && payload) {
				question.media = findMediaImgAndUpdate(question.media || [], payload)
			}
			return true
		}
		case UPDATE_QUESTION_ACTIONS.CHANGE_MEDIA_CHOICE: {
			const payload = action.payload
			if (
				question.type !== variantTypes.MULTIPLE_CHOICE &&
				question.type !== variantTypes.MULTIPLE_SELECT
			)
				return false
			const { index, media } = payload
			const choice = question.choices[index]

			if (choice) {
				// @ts-expect-error Expect an editable question to have a localUrl and not a url
				question.choices[index].media = findMediaImgAndUpdate(
					choice.media || [],
					media
				)
				return true
			}
			return false
		}
		case UPDATE_QUESTION_ACTIONS.DELETE_QUESTION_IMAGE: {
			if (!question.media) return false
			question.media = question.media.filter(
				(mediaItem: Media) => mediaItem.type !== 'IMAGE'
			)
			return true
		}
		case UPDATE_QUESTION_ACTIONS.DELETE_IMG_CHOICE: {
			if (
				question.type !== variantTypes.MULTIPLE_CHOICE &&
				question.type !== variantTypes.MULTIPLE_SELECT
			)
				return false
			const { index } = action.payload
			// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
			if (question.choices[index].media) {
				// @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				question.choices[index].media = question.choices[index].media?.filter(
					(mediaItem: Media) => mediaItem.type !== 'IMAGE'
				)
			}
			return true
		}
		case UPDATE_QUESTION_ACTIONS.ADD_DROPDOWN: {
			if (question.type === variantTypes.CLOZE_DROPDOWN) {
				const template = question.template
				const cursorPosition: number =
					typeof action.payload === 'number' ? action.payload : 0

				const newTemplateSegments: TemplateSegment[] = []
				let runningPosition = 0
				let seenInputs = 0
				let hasInserted = false
				const oldDropdowns: { [dropdownId: string]: QuestionChoice[] } = {}
				question.dropdowns.forEach(
					dropdown =>
						(oldDropdowns[dropdown.templateResponseKey] = dropdown.choices)
				)
				const newDropdowns: { [dropdownId: string]: QuestionChoice[] } = {}

				const insertTemplate = (oldTemplateId?: string) => {
					const newInputId = `${++seenInputs}`
					newTemplateSegments.push({
						type: TemplateSegmentType.Input,
						inputId: newInputId,
					})
					if (oldTemplateId == null) {
						hasInserted = true
						newTemplateSegments.push({
							type: TemplateSegmentType.Text,
							text: ' ',
						})
					}
					newDropdowns[newInputId] =
						oldTemplateId && oldDropdowns[oldTemplateId]
							? oldDropdowns[oldTemplateId]
							: []
				}
				parseTemplateStringIntoSegments(template).forEach(segment => {
					if (!hasInserted && runningPosition === cursorPosition) {
						insertTemplate()
					}
					if (segment.type == TemplateSegmentType.Input) {
						runningPosition += 1
						insertTemplate(segment.inputId)
						return
					}
					if (hasInserted) {
						newTemplateSegments.push(segment)
						return
					}
					if (segment.type === TemplateSegmentType.Text) {
						const segmentText = segment.text
						const positionToInsert = cursorPosition - runningPosition
						if (positionToInsert < segmentText.length - 1) {
							newTemplateSegments.push({
								type: TemplateSegmentType.Text,
								text: segmentText.substring(0, positionToInsert),
							})
							insertTemplate()
							newTemplateSegments.push({
								type: TemplateSegmentType.Text,
								text: segmentText.substring(
									positionToInsert,
									segmentText.length
								),
							})
							return
						}
						runningPosition += segmentText.length
						newTemplateSegments.push(segment)
						return
					}
					newTemplateSegments.push(segment)
				})
				if (!hasInserted) {
					insertTemplate()
				}
				question.template = convertTemplateSegmentsToTemplateString(
					newTemplateSegments
				)
				// @ts-expect-error TS2322 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
				question.dropdowns = Object.keys(newDropdowns).map(
					templateResponseKey => ({
						templateResponseKey,
						choices: newDropdowns[templateResponseKey],
					})
				)

				return true
			} else if (question.type === variantTypes.IMAGE_LABELING) {
				const pos = action.payload
				if (typeof pos === 'object' && !isNaN(pos?.x) && !isNaN(pos?.y)) {
					question.dropdowns.push({
						x: pos.x,
						y: pos.y,
						choices: [{ text: '', answer: true }],
					})
				}
				return true
			} else return false
		}
		case UPDATE_QUESTION_ACTIONS.DELETE_DROPDOWN: {
			if (question.type === variantTypes.CLOZE_DROPDOWN) {
				const templateIdToDelete = action.payload

				const previousTemplateSegments = parseTemplateStringIntoSegments(
					question.template
				)

				return applyClozeDropdownDiff(
					question,
					generateTemplateDif(
						previousTemplateSegments,
						previousTemplateSegments.filter(segment => {
							if (segment.type === TemplateSegmentType.Input) {
								return segment.inputId !== templateIdToDelete
							}
							return true
						})
					)
				)
			} else if (question.type === variantTypes.IMAGE_LABELING) {
				const dropdownKey = action.payload
				question.dropdowns = question.dropdowns.filter(
					dropdown => getImageDropdownKey(dropdown) !== dropdownKey
				)

				return true
			}
			return false
		}
		default: {
			return false
		}
	}
	return false
}

//Function that get choices for a question
function getChoices(question: EditableQuestion, dropdownIndex: number) {
	return question.type === variantTypes.MULTIPLE_SELECT ||
		question.type === variantTypes.MULTIPLE_CHOICE
		? question.choices
		: (question.type === variantTypes.CLOZE_DROPDOWN ||
				question.type === variantTypes.IMAGE_LABELING) &&
		  dropdownIndex > -1
		? // @ts-expect-error TS2532 SUPPRESS ERRORS FROM INTRODUCING noUncheckedIndexedAccess
		  question.dropdowns[dropdownIndex].choices
		: []
}
